import { useEffect, useRef, useState } from "react";
import ExplicitDeductionModalForm from "../modals/ExplicitDeductionModalForm";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
	useDeleteExplicitDeduction,
	useGetExplicitDeduction,
	useGetExplicitDeductionSearch,
} from "../../../../services/explicit-deduction.service";
import {
	APPLY_ON,
	ExplicitDeductionDto,
} from "../../../../models/explicit-deduction.dto";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../helpers/usePermissionManager";
import UploadExplicitDeductions from "../modals/UploadExplicitDeductions";
import ExportExplicitDeductionsModal from "../modals/ExportExplicitDeductionsModal";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import {
	explicitDeductionsColumns,
	explicitDeductionsColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

const ExplicitDeductionsView = () => {
	const { currentCompany } = useCompanyStore();
	const { currentUser, userType } = useUserStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const [
		openUploadExplicitDeductionsModal,
		setOpenUploadExplicitDeductionsModal,
	] = useState(false);
	const [
		openExportExplicitDeductionsModal,
		setOpenExportExplicitDeductionsModal,
	] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(explicitDeductionsColumnVisibility);

	const { mutate: searchExplicitDeductions } = useGetExplicitDeductionSearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<ExplicitDeductionDto>({
		fetchHook: useGetExplicitDeduction,
		options: { companyId: currentCompany?.id },
	});

	// Fetched data for select fields requiring data from database
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: EmployeeBandOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteExplicitDeduction } = useDeleteExplicitDeduction();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteExplicitDeduction(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchExplicitDeductions}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{ title: "Name", fieldName: "name" },
									{ title: "Code", fieldName: "code" },
									{ title: "Date Created", fieldName: "createdAt" },
									{ title: "Date Modified", fieldName: "modifiedAt" },
								],
							},
							{
								title: "Apply On",
								fieldName: "applyOn",
								fieldType: "CHECKBOX_LIST",
								options: APPLY_ON,
							},
						]}
					/>
					<ManageTableColumns
						columns={explicitDeductionsColumns}
						defaultVisibilityModel={explicitDeductionsColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					<div className="relative" ref={dropdownRef}>
						<button
							onClick={() => setIsOpen(!isOpen)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex ml-4 items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<Upload className="mr-3 text-[17px] text-gray-500" />
							Upload/Export
							<ChevronDown
								className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
									isOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{isOpen && (
							<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
								<div className="p-2 space-y-2">
									<PermissionChecker
										permissionRule="OR"
										permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
									>
										<button
											onClick={() => {
												setIsOpen(false);
												setOpenUploadExplicitDeductionsModal(true);
											}}
											className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
									   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
										>
											<GoUpload className="text-[18px] text-gray-500" />
											Upload Explicit Deductions
										</button>
									</PermissionChecker>
									<button
										onClick={() => {
											setIsOpen(false);
											setOpenExportExplicitDeductionsModal(true);
										}}
										className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
									 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
									>
										<BsDownload className="text-[18px] text-gray-500" />
										Export Explicit Deductions
									</button>
								</div>
							</div>
						)}
					</div>
					<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
						<div
							className="flex items-center text-primary-blue cursor-pointer"
							onClick={handleOpenModal}
						>
							<AiOutlinePlus className="mr-1 text-base" />
							<span className="text-sm">Add New Explicit Deduction</span>
						</div>
					</PermissionChecker>
				</div>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.EXPLICIT_DEDUCTIONS}
				data={tableData}
				columns={explicitDeductionsColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={
					checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
						? (row, event) => {
								setFormData(row);
								setAnchorEl(event.currentTarget);
							}
						: undefined
				}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={explicitDeductionsColumnVisibility}
				pageSize={10}
				hasPermission={checkUserPermissionsAnd([
					PERMISSIONS.COMPANY_CONFIGS_READ,
				])}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<ExplicitDeductionModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditForm
							? "Update Explicit Deduction"
							: "Add New Explicit Deduction"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					payPeriods={payPeriods?.data || []}
					employees={employees?.data || []}
					employeeBands={employeeBands?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadExplicitDeductionsModal && (
				<UploadExplicitDeductions
					open={openUploadExplicitDeductionsModal}
					setOpen={setOpenUploadExplicitDeductionsModal}
				/>
			)}
			{openExportExplicitDeductionsModal && currentCompany?.id && (
				<ExportExplicitDeductionsModal
					open={openExportExplicitDeductionsModal}
					setOpen={setOpenExportExplicitDeductionsModal}
					companyId={currentCompany.id}
				/>
			)}
		</div>
	);
};

export default ExplicitDeductionsView;
